import moment from "moment-timezone";

export const DEFAULT_DATE_FORMAT = "MM/DD/YYYY";

// Ledger dates are calendar dates (YYYY-MM-DD). Parse them strictly and never through a timezone,
// so a date never shifts by a day.
export const parseLedgerDate = (value) => {
    if (!value || typeof value !== "string") return null;
    const date = moment(value.slice(0, 10), "YYYY-MM-DD", true);
    return date.isValid() ? date : null;
};

export const formatLedgerDate = (value, dateFormat) => {
    const date = parseLedgerDate(value);
    return date ? date.format(dateFormat || DEFAULT_DATE_FORMAT) : value || "";
};

// A Date from the date picker -> YYYY-MM-DD in the browser's calendar (what the user picked).
export const toLedgerDate = (value) => (value ? moment(value).format("YYYY-MM-DD") : undefined);

const MINUS = "−";

export const formatHours = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number.toFixed(2) : "";
};

export const formatChange = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return "";
    const fixed = Math.abs(number).toFixed(2);
    if (Number(fixed) === 0) return "0.00";
    return number > 0 ? `+${fixed}` : `${MINUS}${fixed}`;
};

const ADJUSTMENT_SOURCE_KEYS = {
    assign: "ledger-source-assign",
    "update-total": "ledger-source-update-total",
    "update-from-integration": "ledger-source-integration",
    "api-update": "ledger-source-integration",
    "transfer-adjustment": "ledger-source-transfer-adjustment",
};

const periodRange = (row, dateFormat) => {
    if (!row?.periodStartDate || !row?.periodEndDate) return "";
    return `${formatLedgerDate(row.periodStartDate, dateFormat)} - ${formatLedgerDate(row.periodEndDate, dateFormat)}`;
};

const withParens = (text) => (text ? ` (${text})` : "");

// A pay period belongs to the plan active on its last day, so a plan's first period can start
// before the plan does (e.g. 07/19 - 08/01 on a plan starting 08/01).
export const startedBeforePlan = (periodStartDate, planStartDate) => {
    const period = parseLedgerDate(periodStartDate);
    const plan = parseLedgerDate(planStartDate);
    return !!(period && plan && period.isBefore(plan, "day"));
};

const periodWithNote = (row, t, dateFormat) =>
    [
        periodRange(row, dateFormat),
        startedBeforePlan(row.periodStartDate, row.planStartDate)
            ? t("pay-period-started-before-plan", { date: formatLedgerDate(row.planStartDate, dateFormat) })
            : "",
    ]
        .filter(Boolean)
        .join("; ");

// Plain text description of a ledger row, shared by the table and the CSV export.
export const describeLedgerRow = (row, t, dateFormat) => {
    switch (row?.type) {
        case "opening":
            return t("ledger-beginning-balance");
        case "accrual": {
            let text = `${t("ledger-accrual")}${withParens(periodWithNote(row, t, dateFormat))}`;
            if (Number(row.lost) > 0) {
                text += ` ${t("ledger-over-cap", { hours: formatHours(row.lost) })}`;
            }
            return text;
        }
        case "used":
            return row.source === "lock-pay-group"
                ? `${t("ledger-used-correction")}${withParens(periodWithNote(row, t, dateFormat))}`
                : t("ledger-used");
        case "adjustment": {
            const key = ADJUSTMENT_SOURCE_KEYS[row.source];
            const label = key ? t(key) : row.source;
            let text = label ? `${t("ledger-adjustment")}: ${label}` : t("ledger-adjustment");
            if (row.comment) text += ` - ${row.comment}`;
            return text;
        }
        case "transfer":
            return t("ledger-transfer", { from: row.planCode || "?", to: row.toPlanCode || "?" });
        case "year-end": {
            let text = t("ledger-year-end", { from: row.planCode || "?", to: row.toPlanCode || "?" });
            if (Number(row.change) < 0) {
                text += ` ${t("ledger-forfeited-hours", { hours: formatHours(Math.abs(row.change)) })}`;
            }
            return text;
        }
        case "not-carried":
            return t("ledger-not-carried", { from: row.planCode || "?", to: row.toPlanCode || "?" });
        case "unrecorded":
            return t("ledger-unrecorded");
        case "pending": {
            const source =
                row.source === "request"
                    ? t("ledger-pending-request")
                    : row.source === "time_sheet" || row.source === "timesheet"
                      ? t("ledger-pending-timesheet")
                      : row.source === "transfer-adjustment"
                        ? t("ledger-pending-transfer-adjustment", { from: row.fromPlanCode || "?" })
                        : row.source;
            const range =
                row.periodStartDate && row.periodEndDate && row.periodStartDate !== row.periodEndDate
                    ? periodRange(row, dateFormat)
                    : "";
            return `${t("ledger-pending")}${withParens([source, range].filter(Boolean).join(", "))}`;
        }
        default:
            return row?.type || "";
    }
};

// Table rows: a "Beginning balance" row at `from`, then the backend rows in order.
export const buildLedgerRows = (ledger) => {
    if (!ledger) return [];
    const rows = Array.isArray(ledger.rows) ? ledger.rows : [];
    const planStart = new Map((ledger.chain || []).map((plan) => [plan.id, plan.startDate]));
    return [
        {
            id: "opening",
            type: "opening",
            date: ledger.from,
            planCode: rows[0]?.planCode || ledger.chain?.[0]?.code,
            change: null,
            balance: ledger.summary?.opening ?? 0,
        },
        ...rows.map((row, index) => ({ ...row, planStartDate: planStart.get(row.plan), id: `row-${index}` })),
    ];
};

const csvCell = (value) => {
    if (value === null || value === undefined) return "";
    const text = String(value);
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const buildLedgerCsv = (ledger, t, dateFormat) => {
    const header = [
        t("date"),
        t("plan"),
        t("ledger-type"),
        t("description"),
        t("ledger-change-hours"),
        t("ledger-balance"),
    ];
    const lines = buildLedgerRows(ledger).map((row) => [
        row.date || "",
        row.planCode || "",
        row.type,
        describeLedgerRow(row, t, dateFormat),
        row.change === null || row.change === undefined ? "" : formatHours(row.change),
        row.type === "pending" ? "" : formatHours(row.balance),
    ]);
    return [header, ...lines].map((line) => line.map(csvCell).join(",")).join("\r\n");
};

export const downloadCsv = (content, filename) => {
    // BOM so Excel opens the file as UTF-8
    const blob = new Blob(["﻿", content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

export const ledgerCsvFilename = (ledger) => {
    const parts = [
        "leave-balance",
        ledger?.employee?.employeeNumber,
        ledger?.rows?.find((row) => row.plan === ledger?.plan)?.planCode ||
            ledger?.chain?.[ledger.chain.length - 1]?.code,
        ledger?.from,
        ledger?.to,
    ].filter(Boolean);
    return `${parts.join("_").replace(/[^\w.-]+/g, "-")}.csv`;
};
